//=================================================
// Collection (Art and Artists)
//=================================================

import { createInitializeOnce, securePutRequest, track } from './utilities';
import { adjustFootnotes } from './footnotes';
import { updateFormControls, runSearch } from './lists';

const initializeOnce = createInitializeOnce();

// Validate that the date selection (years) make sense
function validateCollectionDates(dateWrapper) {
    const [dateOne, dateTwo] = Array.from(dateWrapper.querySelectorAll('input[type="number"]'), (el) => el.value);
    const dateOneParsed = parseInt(dateOne, 10);
    const dateTwoParsed = parseInt(dateTwo, 10);

    // Make sure the dates are invalid years, and the inputs not empty, before throwing an error
    const invalid = (Number.isNaN(dateOneParsed) && dateOne !== '') ||
        (Number.isNaN(dateTwoParsed) && dateTwo !== '') ||
        dateOneParsed > dateTwoParsed;

    dateWrapper.classList.toggle('collection-overlay-dates--error', invalid);
    if (!invalid) {
        dateWrapper.classList.remove('collection-overlay-dates--explanation');
    }
}

// Exclusive filter button group (classifications, colors)
// Clicking a button makes it the active filter + syncs the hidden form input, clicking the active one clears it
function setupFilterButtons(wrapper, { containerSelector, buttonSelector, inputSelector, activateLocally, analyticsValue }) {
    const container = wrapper.querySelector(containerSelector);
    if (!initializeOnce(container)) return;

    // Delegate button clicks since these are added dynamically
    container.addEventListener('click', (e) => {
        const button = e.target.closest(buttonSelector);
        const input = wrapper.querySelector(inputSelector);
        if (!button || !input) return;

        // If button is active disable the filter
        if (button.classList.contains('active')) {
            button.classList.remove('active');
            button.setAttribute('aria-pressed', 'false');
            // Set hidden form input
            input.value = '';
        } else {
            // ...otherwise set the filter to active (and deactivate any active filters)
            wrapper.querySelectorAll(buttonSelector).forEach((el) => {
                // For classifications, not applying the active class here and instead letting
                // it come in over the turbo stream leads to less "blinking"
                if (activateLocally) el.classList.remove('active');
                el.setAttribute('aria-pressed', 'false');
            });
            if (activateLocally) button.classList.add('active');
            button.setAttribute('aria-pressed', 'true');

            // Set hidden form input
            input.value = button.dataset.value;

            if (button.dataset.value) {
                track('collection', 'filter', analyticsValue(button));
            }
        }

        updateFormControls(wrapper);
        input.dispatchEvent(new Event('input'));
    });
}

// Dual-thumb year range slider that writes through to the year number inputs
// The ranges are unnamed so they never submit; search runs via their bubbling input events (debounced in lists.js)
function setupYearSlider(wrapper) {
    const slider = wrapper.querySelector('.collection-year-slider');
    if (!slider || !initializeOnce(slider)) {
        return;
    }

    const fill = slider.querySelector('.collection-year-slider__fill');
    const lowRange = slider.querySelector('.collection-year-slider__range--low');
    const highRange = slider.querySelector('.collection-year-slider__range--high');
    const lowInput = wrapper.querySelector('#suggestion-date-low');
    const highInput = wrapper.querySelector('#suggestion-date-high');
    const min = parseInt(slider.dataset.min, 10);
    const max = parseInt(slider.dataset.max, 10);

    // Draw the fill bar between the thumbs; raise the low thumb when it's in the right half so it stays grabbable at the max edge
    function render() {
        fill.style.left = `${ ((lowRange.value - min) / (max - min)) * 100 }%`;
        fill.style.right = `${ ((max - highRange.value) / (max - min)) * 100 }%`;
        lowRange.classList.toggle('collection-year-slider__range--on-top', lowRange.value - min > (max - min) / 2);
    }

    // Typed years move the thumbs; empty fields park them at the ends (the browser clamps out-of-range values)
    function syncFromInputs() {
        lowRange.value = lowInput.value || min;
        highRange.value = highInput.value || max;
        render();
    }

    // Thumbs write through to the year fields without crossing; a thumb parked at its own end of the track means "no filter"
    lowRange.addEventListener('input', () => {
        if (parseInt(lowRange.value, 10) > parseInt(highRange.value, 10)) {
            lowRange.value = highRange.value;
        }
        lowInput.value = parseInt(lowRange.value, 10) === min ? '' : lowRange.value;
        render();
    });
    highRange.addEventListener('input', () => {
        if (parseInt(highRange.value, 10) < parseInt(lowRange.value, 10)) {
            highRange.value = lowRange.value;
        }
        highInput.value = parseInt(highRange.value, 10) === max ? '' : highRange.value;
        render();
    });

    // Track once per adjustment on change (input fires continuously while dragging)
    slider.addEventListener('change', (e) => {
        track('collection', 'filter', e.target.dataset.label);
    });

    [lowInput, highInput].forEach((el) => el.addEventListener('input', syncFromInputs));

    // Reset/Show-all clear the year fields in lists.js; defer so the thumbs sync after that runs
    wrapper.addEventListener('click', (e) => {
        if (e.target.closest('.list__reset, .list__all')) {
            setTimeout(syncFromInputs, 0);
        }
    });

    syncFromInputs();
}

// Upload an image and search by its embedding
// While an image is loaded the search form is pointed at the image-search endpoint (multipart POST)
// (so a normal submit becomes an image search and Turbo handles the request)
function setupImageSearch(wrapper) {
    const imageSearch = wrapper.querySelector('.collection-image-search');
    if (!initializeOnce(imageSearch)) {
        return;
    }

    const form = wrapper.querySelector('form');
    const input = imageSearch.querySelector('.collection-image-search__input');
    const preview = imageSearch.querySelector('.collection-image-search__preview');
    const clear = imageSearch.querySelector('.collection-image-search__clear');
    const filename = imageSearch.querySelector('.collection-image-search__filename');
    const emptyLabel = filename?.textContent; // "No file chosen" placeholder
    const defaultAction = form?.getAttribute('action');
    const defaultMethod = form?.getAttribute('method') || 'get';

    // Point the form at the image-search endpoint (POST) while an image is loaded, else restore it
    // enctype is required: Turbo only sends file inputs when the form is multipart; otherwise it serializes to URLSearchParams and the image is silently dropped
    function setImageMode(on) {
        form?.setAttribute('method', on ? 'post' : defaultMethod);
        form?.setAttribute('action', on ? imageSearch.dataset.url : defaultAction);
        if (on) {
            form?.setAttribute('enctype', 'multipart/form-data');
        } else {
            form?.removeAttribute('enctype');
        }
    }

    // Show the picked image or clear it
    function setPreview(file) {
        if (preview.src.startsWith('blob:')) {
            URL.revokeObjectURL(preview.src);
        }
        preview.hidden = !file;
        if (file) {
            preview.src = URL.createObjectURL(file);
        } else {
            preview.removeAttribute('src');
        }
    }

    function clearImage() {
        input.value = ''; // clears input.files, so it stops counting as an active filter
        setPreview(null);
        if (filename) filename.textContent = emptyLabel;
        clear?.classList.remove('active');
        setImageMode(false);
    }

    input?.addEventListener('change', () => {
        const file = input.files?.[0];
        if (!file) {
            return;
        }
        setPreview(file);
        if (filename) filename.textContent = file.name;
        clear?.classList.add('active');
        setImageMode(true);
        runSearch(wrapper); // submits the form, now a native Turbo POST to image_search
    });

    clear?.addEventListener('click', () => {
        clearImage();
        runSearch(wrapper); // back to the normal relevance search
    });

    // Global Reset / Show-all clear named fields; keep the image UI + form target in sync
    wrapper.addEventListener('click', (e) => {
        if (e.target.closest('.list__reset, .list__all')) {
            clearImage();
        }
    });
}

export function setupCollectionSearch() {
    const wrapper = document.querySelector('.list--collection');
    if (!wrapper) {
        return;
    }

    setupFilterButtons(wrapper, {
        containerSelector: '.list-search__field-wrapper--classifications',
        buttonSelector: '.classifications__btn',
        inputSelector: '[name="q[classification_cont]"]',
        activateLocally: false,
        analyticsValue: (button) => button.dataset.value.toLowerCase(),
    });
    setupFilterButtons(wrapper, {
        containerSelector: '.list-search__field-wrapper--color',
        buttonSelector: '.color-selector',
        inputSelector: '[name="q[color]"]',
        activateLocally: true,
        analyticsValue: (button) => `color (${ button.dataset.label })`,
    });
    setupImageSearch(wrapper);
    setupYearSlider(wrapper);
    updateFormControls(wrapper);

    // Date validation
    const dateWrapper = wrapper.querySelector('.collection-overlay-dates');
    if (initializeOnce(dateWrapper)) {
        dateWrapper.addEventListener('change', () => {
            validateCollectionDates(dateWrapper);
        });

        // Listen for invalid input and show an explanation of why the key was suppressed
        // See: https://www.w3.org/WAI/WCAG21/Understanding/error-identification.html
        dateWrapper.addEventListener('keydown', (e) => {
            // Let named keys (Enter, Backspace, arrows...) and shortcuts through
            if (e.key.length !== 1 || e.ctrlKey || e.metaKey) {
                return;
            }
            if (Number.isNaN(parseInt(e.key, 10))) {
                e.preventDefault();
                dateWrapper.classList.add('collection-overlay-dates--explanation');
            } else {
                dateWrapper.classList.remove('collection-overlay-dates--explanation');
            }
        });
    }

    // Exhibition filter
    const exhibitionFilter = wrapper.querySelector('.collection-exhibition-filter');
    const exhibitionFilterInput = wrapper.querySelector('.collection-exhibition-filter__input');
    if (exhibitionFilter && exhibitionFilterInput && initializeOnce(exhibitionFilter)) {
        exhibitionFilter.addEventListener('click', () => {
            exhibitionFilterInput.value = '';
            exhibitionFilterInput.dispatchEvent(new Event('input'));
            exhibitionFilter.remove();
        });
    }

    // Global Reset / Show-all also removes the exhibition filter chip
    // (its hidden input is cleared by the generic field reset in lists.js)
    if (initializeOnce(wrapper)) {
        wrapper.addEventListener('click', (e) => {
            if (e.target.closest('.list__reset, .list__all')) {
                wrapper.querySelectorAll('.collection-exhibition-filter').forEach((el) => el.remove());
            }
        });
    }

    // Add some usage tracking (sliders track on change instead since input fires per drag tick)
    // NOTE: the search input is guarded here too, so its blur tracking has to be bound in the same pass
    wrapper.querySelectorAll('input:not([type="range"]), select').forEach((el) => {
        if (initializeOnce(el)) {
            el.addEventListener('input', () => {
                track('collection', 'filter', el.dataset.label);
            });

            // Track searches on blur since that should mean someone is done typing
            if (el.type === 'search') {
                el.addEventListener('blur', () => {
                    if (el.value !== '') {
                        track('collection', 'search', el.value);
                    }
                });
            }
        }
    });

    wrapper.querySelectorAll('.list__randomize').forEach((el) => {
        if (initializeOnce(el)) {
            el.addEventListener('click', () => {
                track('collection', 'randomize', 'true');
            });
        }
    });
}

// Artists with bios and artworks with visual descriptions have toggling behavior
export function setupCollectionExpandables() {
    document.querySelectorAll('.collection-bio, .collection-detail-expandable').forEach((el) => {
        if (!initializeOnce(el)) return;

        el.querySelectorAll('.collection-bio__toggle, .collection-detail-expandable__toggle').forEach((toggle) => {
            toggle.addEventListener('click', () => {
                const expanded = toggle.getAttribute('aria-expanded') === 'true';
                toggle.setAttribute('aria-expanded', String(!expanded));
                el.classList.toggle('active', !expanded);
                toggle.textContent = expanded ? 'Read more' : 'Read less';

                if (!expanded) {
                    adjustFootnotes();
                }
            });
        });
    });
}

// On mobile only. Same approach as the event sticky header (see setupTicketSelector).
export function setupCollectionStickyTitle() {
    document.querySelectorAll('.artwork-sticky-title').forEach((sticky) => {
        const title = document.querySelector('.artwork-label__title');

        if (!title || !initializeOnce(sticky)) return;

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                const scrolledPast = entry.boundingClientRect.bottom <= (entry.rootBounds?.top ?? 0);

                sticky.classList.toggle('artwork-sticky-title--visible', !entry.isIntersecting && scrolledPast);
            });
        });

        observer.observe(title);
    });
}

// Collection sync listeners used in the admin and admin toolbar
export function setupSyncs() {
    // Sync collection records
    document.querySelectorAll('.collection-sync').forEach((el) => {
        if (!initializeOnce(el)) return;

        el.addEventListener('click', (e) => {
            e.preventDefault();

            document.querySelectorAll('.collection-sync').forEach((ele) => {
                ele.disabled = true;
                ele.classList.add('syncing');
            });
            securePutRequest('/admin/sync/collection');
        });
    });

    // Sync records
    document.querySelectorAll('.record-sync').forEach((el) => {
        if (!initializeOnce(el)) return;

        el.addEventListener('click', (e) => {
            e.preventDefault();

            const { url } = el.dataset;
            if (url) {
                document.querySelectorAll('.record-sync').forEach((ele) => {
                    if (ele.dataset.url === url) {
                        ele.disabled = true;
                        ele.classList.add('syncing');
                    }
                });

                securePutRequest(url);
            }
        });
    });
}
